import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Loader2, X, Tag, AlertTriangle, Filter } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { VehicleCard, VehicleCardSkeleton } from '@/components/VehicleCard';
import { FilterSidebar } from '@/components/FilterSidebar';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const HERO_BG = "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=80";

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageSize, setPageSize] = useState(6);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 6,
    total: 0,
    total_pages: 1
  });

  // Scroll restoration: read pending state once on mount
  const pendingScrollRef = useRef(null);
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('catalogScroll');
      if (saved) {
        const data = JSON.parse(saved);
        if (data.url === window.location.pathname + window.location.search) {
          pendingScrollRef.current = data;
        }
        sessionStorage.removeItem('catalogScroll');
      }
    } catch {}
  }, []);

  // Parse filters from URL
  const getFiltersFromURL = useCallback(() => {
    const filters = {};
    for (const [key, value] of searchParams.entries()) {
      if (key === 'page') {
        filters.page = parseInt(value);
      } else if (['precio_min', 'precio_max', 'modelo_min', 'modelo_max'].includes(key)) {
        filters[key] = parseFloat(value);
      } else if (key === 'destacado') {
        filters[key] = value === 'true';
      } else {
        filters[key] = value;
      }
    }
    return filters;
  }, [searchParams]);

  // Restore filters: URL params take priority, then sessionStorage
  const getInitialFilters = useCallback(() => {
    const urlFilters = getFiltersFromURL();
    if (Object.keys(urlFilters).length > 0) return urlFilters;
    try {
      const saved = sessionStorage.getItem('vehicleFilters');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  }, [getFiltersFromURL]);

  const [filters, setFilters] = useState(getInitialFilters);

  // Sync URL params with stored filters on mount (restore URL if filters exist)
  const initialSyncDone = useRef(false);
  useEffect(() => {
    if (initialSyncDone.current) return;
    initialSyncDone.current = true;
    const urlFilters = getFiltersFromURL();
    if (Object.keys(urlFilters).length === 0) {
      try {
        const saved = sessionStorage.getItem('vehicleFilters');
        if (saved) {
          const restored = JSON.parse(saved);
          if (Object.keys(restored).length > 0) {
            const params = new URLSearchParams();
            Object.entries(restored).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '' && key !== 'page') {
                params.set(key, value.toString());
              }
            });
            setSearchParams(params);
          }
        }
      } catch {}
    }
  }, [getFiltersFromURL, setSearchParams]);

  // Sync filters when URL params change
  const location = useLocation();
  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    setFilters(urlFilters);
    // Scroll to vehicles section when coming from search (#vehiculos)
    if (location.hash === '#vehiculos') {
      setTimeout(() => {
        document.getElementById('vehicles-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [searchParams, getFiltersFromURL, location.hash]);

  // Fetch filter options and settings
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [filtersData, settingsData] = await Promise.all([
          api.getFilters().catch(() => null),
          api.getSettings().catch(() => ({ vehicles_per_page: 6 }))
        ]);
        if (filtersData) setFilterOptions(filtersData);
        if (settingsData?.vehicles_per_page) setPageSize(settingsData.vehicles_per_page);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch vehicles (initial load or filter change)
  const fetchVehicles = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      setVehicles([]);
    }
    
    try {
      const data = await api.getVehicles({
        page: 1,
        page_size: pageSize,
        status: 'publicado',
        ...filters
      });
      
      setVehicles(data.vehicles);
      setPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages
      });
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Error al cargar los vehiculos');
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize]);

  // Load more vehicles
  const loadMoreVehicles = useCallback(async () => {
    if (loadingMore || vehicles.length >= pagination.total) return;

    setLoadingMore(true);
    try {
      const nextPage = pagination.page + 1;
      const data = await api.getVehicles({
        page: nextPage,
        page_size: pageSize,
        status: 'publicado',
        ...filters
      });

      setVehicles(prev => [...prev, ...data.vehicles]);
      setPagination({
        page: data.page,
        page_size: data.page_size,
        total: data.total,
        total_pages: data.total_pages
      });
    } catch (error) {
      console.error('Error loading more vehicles:', error);
      toast.error('Error al cargar más vehículos');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, vehicles.length, pagination, pageSize, filters]);

  useEffect(() => {
    fetchVehicles(true);
  }, [fetchVehicles]);

  // Scroll restoration: load more pages until reaching saved count, then scroll
  useEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending || loading || loadingMore) return;

    if (vehicles.length < pending.count && vehicles.length < pagination.total) {
      loadMoreVehicles();
      return;
    }

    requestAnimationFrame(() => {
      window.scrollTo({ top: pending.y, behavior: 'auto' });
      pendingScrollRef.current = null;
    });
  }, [vehicles.length, loading, loadingMore, pagination.total, loadMoreVehicles]);

  // Update URL when filters change
  const handleFilterChange = (newFilters) => {
    const params = new URLSearchParams();
    const toSave = {};
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'page') {
        params.set(key, value.toString());
        toSave[key] = value;
      }
    });
    setSearchParams(params);
    setFilters(newFilters);
    sessionStorage.setItem('vehicleFilters', JSON.stringify(toSave));
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setFilters({});
    sessionStorage.removeItem('vehicleFilters');
  };

  const hasActiveFilters = Object.keys(filters).some(k => k !== 'page' && filters[k] !== '' && filters[k] !== undefined && filters[k] !== null);
  const hasMoreVehicles = vehicles.length < pagination.total;
  const [filtersHidden, setFiltersHidden] = useState(false);

  // Infinite scroll: load one batch when sentinel enters viewport
  const sentinelRef = useRef(null);
  const loadTriggered = useRef(false);

  useEffect(() => {
    // Reset trigger when new data arrives (loadingMore goes false)
    if (!loadingMore) loadTriggered.current = false;
  }, [loadingMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingMore && hasMoreVehicles && !loadTriggered.current) {
          loadTriggered.current = true;
          loadMoreVehicles();
        }
      },
      { rootMargin: '100px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadingMore, hasMoreVehicles, loadMoreVehicles]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Hero Section */}
      <section 
        className="hero-section"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="hero-section"
      >
        <div className="hero-overlay" />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10 pb-8 md:pb-24">
          <div className="max-w-2xl animate-slide-up">
            <p className="text-primary text-sm font-bold uppercase tracking-widest mb-4">
              Vehículos Chocados en GDL
            </p>
            <h1 className="mb-6">
              <img
                src="/logo.png"
                alt="Chocados GDL"
                className="w-auto h-32 md:h-48 lg:h-56 max-w-full"
              />
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-lg">
              Autos, camionetas, tractocamiones y más. Imágenes de alta resolución para ver cada detalle.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                className="btn-primary"
                onClick={() => {
                  handleClearFilters();
                  document.getElementById('vehicles-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="hero-cta"
              >
                Ver Inventario
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider"
                onClick={() => navigate('/inventario/oferta')}
                data-testid="hero-ofertas"
              >
                <Tag className="w-5 h-5 mr-2" />
                Ver Ofertas
              </Button>
              <Button
                className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold uppercase tracking-wider"
                onClick={() => navigate('/inventario/autopartes')}
                data-testid="hero-autopartes"
              >
                <AlertTriangle className="w-5 h-5 mr-2" />
                Autopartes
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="border-white/30 text-white hover:bg-white/10 hover:text-white"
                >
                  <X className="w-4 h-4 mr-2" />
                  Borrar Filtros
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Vehicles Section */}
      <section id="vehicles-section" className="py-16 md:py-24" data-testid="vehicles-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-primary text-sm font-bold uppercase tracking-widest mb-2">
                Nuestro Inventario
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-foreground">
                Vehículos Disponibles
              </h2>
            </div>
            <p className="text-muted-foreground">
              Mostrando <span className="text-primary font-bold">{vehicles.length}</span> de{' '}
              <span className="text-primary font-bold">{pagination.total}</span> vehículos
            </p>
          </div>

          {/* Desktop filter toggle (when hidden) */}
          {filtersHidden && (
            <div className="hidden lg:flex mb-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setFiltersHidden(false)}
                className="gap-2"
              >
                <Filter className="w-4 h-4" />
                Mostrar Filtros
              </Button>
            </div>
          )}

          {/* Content Layout */}
          <div className="flex gap-8">
            {/* Filters */}
            <FilterSidebar
              filters={filters}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              vehicleCount={pagination.total}
              desktopHidden={filtersHidden}
              onToggleDesktop={() => setFiltersHidden(true)}
            />

            {/* Vehicle Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[...Array(pageSize)].map((_, i) => (
                    <VehicleCardSkeleton key={i} />
                  ))}
                </div>
              ) : vehicles.length > 0 ? (
                <>
                  <div id="vehicles-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vehicles-grid">
                    {vehicles.map((vehicle, index) => (
                      <VehicleCard key={vehicle.id} vehicle={vehicle} index={index} />
                    ))}
                  </div>

                  {/* Infinite scroll sentinel */}
                  {hasMoreVehicles && (
                    <div ref={sentinelRef} className="flex justify-center items-center mt-12 h-16">
                      {loadingMore && <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16" data-testid="no-results">
                  <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    No se encontraron vehículos
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Intenta ajustar los filtros de búsqueda
                  </p>
                  <Button onClick={handleClearFilters} className="bg-primary hover:bg-primary/90 text-white font-bold px-6 py-2">
                    Limpiar Filtros
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
